/*
  Autoplay, looping and variable height for mobile sliders.

  Dawn's SliderComponent handles the scroll-snap row and prev/next buttons but
  has no autoplay, wrap-around or adaptive height (SlideshowComponent has the
  first two, but is coupled to slideshow markup). This layers them on top of any
  <slider-component> marked with data-mobile-slider, active only below 990px
  where the mobile slider exists. data-slider-query swaps in a different media
  query, for sliders that also run on desktop.

  Shared by the Multi-column, Collection Trust Badges and Related products
  sections. Sliders added after load (e.g. by product-recommendations) are set
  up with window.initMobileSliders(container).
*/

(function () {
  const MOBILE_QUERY = '(max-width: 989px)';
  const EDGE_TOLERANCE = 2;

  class MobileSlider {
    constructor(root) {
      this.root = root;
      this.slider = root.querySelector('[id^="Slider-"]');
      this.prevButton = root.querySelector('button[name="previous"]');
      this.nextButton = root.querySelector('button[name="next"]');
      if (!this.slider) return;

      this.loop = root.dataset.loop === 'true';
      this.variableHeight = root.dataset.variableHeight === 'true';
      this.autoplayDelay = (parseInt(root.dataset.autoplay, 10) || 0) * 1000;
      this.timer = null;
      this.paused = false;

      this.mobile = window.matchMedia(root.dataset.sliderQuery || MOBILE_QUERY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (this.loop && this.nextButton) {
        this.root.addEventListener('click', this.onCaptureClick.bind(this), true);
      }

      if (this.autoplayDelay > 0) {
        ['mouseenter', 'focusin', 'touchstart'].forEach((type) =>
          this.root.addEventListener(type, () => this.pause(), { passive: true })
        );
        ['mouseleave', 'focusout'].forEach((type) =>
          this.root.addEventListener(type, () => this.resume())
        );
        document.addEventListener('visibilitychange', () =>
          document.hidden ? this.pause() : this.resume()
        );

        // Don't autoplay a slider that isn't on screen.
        if ('IntersectionObserver' in window) {
          new IntersectionObserver((entries) => {
            entries.forEach((entry) => (entry.isIntersecting ? this.resume() : this.pause()));
          }).observe(this.root);
        }
      }

      if (this.variableHeight) {
        this.onScrollHeight = this.onScrollHeight.bind(this);
        this.slider.addEventListener('scroll', this.onScrollHeight, { passive: true });
        window.addEventListener('resize', this.onScrollHeight);
        window.addEventListener('load', () => this.applyHeight());
        // Slide heights depend on images, so remeasure as they arrive.
        this.slider.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', () => this.applyHeight(), { once: true });
        });
      }

      this.mobile.addEventListener('change', () => this.sync());
      this.sync();
    }

    get isActive() {
      return this.mobile.matches;
    }

    get atStart() {
      return this.slider.scrollLeft <= EDGE_TOLERANCE;
    }

    get atEnd() {
      return (
        this.slider.scrollLeft + this.slider.clientWidth >=
        this.slider.scrollWidth - EDGE_TOLERANCE
      );
    }

    get step() {
      // SliderComponent measures this; fall back to the first slide's width.
      const component = this.root;
      if (component.sliderItemOffset) return component.sliderItemOffset;
      const first = this.slider.querySelector('[id^="Slide-"]');
      return first ? first.clientWidth : this.slider.clientWidth;
    }

    sync() {
      if (this.isActive) {
        if (this.loop) this.enableLooping();
        if (this.variableHeight) this.applyHeight();
        this.start();
      } else {
        this.stop();
        if (this.variableHeight) this.slider.style.removeProperty('height');
      }
    }

    onScrollHeight() {
      if (this.heightFrame) cancelAnimationFrame(this.heightFrame);
      this.heightFrame = requestAnimationFrame(() => this.applyHeight());
    }

    /*
      align-items: flex-start stops each slide stretching, but the flex
      container still sizes to the tallest slide, leaving dead space under the
      short ones. So the container height is pinned to the visible slide.
    */
    applyHeight() {
      if (!this.variableHeight || !this.isActive) return;
      const slides = Array.from(this.slider.querySelectorAll('[id^="Slide-"]'));
      if (slides.length < 2) return;

      const active = slides.reduce((closest, slide) => {
        const delta = Math.abs(slide.offsetLeft - this.slider.scrollLeft);
        return delta < closest.delta ? { slide, delta } : closest;
      }, { slide: slides[0], delta: Infinity }).slide;

      // Measure the slide, not the card inside it: slider--tablet adds vertical
      // padding to the slide for focus outlines and shadows, and leaving it out
      // makes the container too short, which turns the content into a
      // vertically scrollable area.
      const height = Math.ceil(active.getBoundingClientRect().height);
      if (height > 0) this.slider.style.height = `${height}px`;
    }

    enableLooping() {
      this.root.enableSliderLooping = true;
      if (this.prevButton) this.prevButton.removeAttribute('disabled');
      if (this.nextButton) this.nextButton.removeAttribute('disabled');
    }

    onCaptureClick(event) {
      if (!this.isActive) return;
      const button = event.target.closest('button[name="next"], button[name="previous"]');
      if (!button || !this.root.contains(button)) return;

      const isNext = button.name === 'next';
      if ((isNext && !this.atEnd) || (!isNext && !this.atStart)) return;

      event.preventDefault();
      event.stopPropagation();
      this.scrollTo(isNext ? 0 : this.slider.scrollWidth);
      this.restart();
    }

    advance() {
      if (this.atEnd) {
        if (!this.loop) return this.stop();
        return this.scrollTo(0);
      }
      this.scrollTo(this.slider.scrollLeft + this.step);
    }

    scrollTo(left) {
      this.slider.scrollTo({ left, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }

    start() {
      if (this.autoplayDelay <= 0 || this.timer || this.paused) return;
      if (this.reducedMotion.matches) return;
      this.timer = setInterval(() => this.advance(), this.autoplayDelay);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      if (this.isActive) this.start();
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-mobile-slider]').forEach((el) => {
      if (el.mobileSlider) return;
      el.mobileSlider = new MobileSlider(el);
    });
  }

  window.initMobileSliders = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
